import { Gender } from "@/models/club";
import { useUserFormContext } from "@/pages/dashboard/teams/teamMembers/add/Context";
import PersianUtil from "@/services/persianUtiles/numberPersianUtiles";
import {
  ActionIcon,
  ColorPicker,
  DEFAULT_THEME,
  Grid,
  Image,
  InputBase,
  LoadingOverlay,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPencil, IconTrash, IconUser } from "@tabler/icons-react";
import imageCompression from "browser-image-compression";
import { useState } from "react";
import ImageUploading, { ImageListType } from "react-images-uploading";
import BirthDateSelector from "@/components/birthDateSelector";

interface TeamMemberProfileInformationProps {
  readonly?: boolean;
}

function TeamMemberProfileInformation({
  readonly = false,
}: TeamMemberProfileInformationProps) {
  const form = useUserFormContext();

  const [uploaderLoading, setUploaderLoading] = useState<boolean>();

  const resizeImage = async (file: File) => {
    const options = {
      maxSizeMB: 0.5, // Compress to a maximum size
      maxWidthOrHeight: 1000, // Resize maintaining aspect ratio
      useWebWorker: true,
      fileType: file.type || "image/jpeg", // Preserve original file type
      initialQuality: 0.8, // Maintain good quality
    };

    try {
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      console.error("Error resizing image:", error);
      throw error;
    }
  };

  const onChange = async (imageList: ImageListType) => {
    setUploaderLoading(true);
    const [imageFile] = imageList;
    if (imageFile?.file) {
      const originalFile = imageFile.file;

      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];
      if (!allowedTypes.includes(originalFile.type)) {
        notifications.show({
          message:
            "نوع فایل مجاز نیست. لطفاً تصویر با فرمت JPG، PNG یا WebP انتخاب کنید.",
          color: "red",
        });
        setUploaderLoading(false);
        return;
      }

      const compressedImage = await resizeImage(originalFile);
      // Preserve original file name and type for proper server validation
      const fileExtension = originalFile.name.split(".").pop() || "jpg";
      const fileName = `profile_image.${fileExtension}`;

      form.setValues({
        profileImage: [
          {
            ...imageFile,
            file: new File([compressedImage], fileName, {
              type: originalFile.type || "image/jpeg",
            }),
          },
        ],
      });
    } else {
      form.setValues({
        profileImage: [],
      });
    }
    setUploaderLoading(false);
  };

  return (
    <Stack>
      <Stack gap="0">
        <Title order={5}>اطلاعات فردی</Title>
        <Text size="sm" pt="xs">
          مدیریت پروفایل شخصی عضو تیم
        </Text>
      </Stack>

      <Stack>
        <ImageUploading
          multiple
          value={form?.values?.profileImage || []}
          onChange={onChange}
          maxNumber={1}
          dataURLKey="data_url"
          acceptType={["jpg", "jpeg", "png", "webp"]}
        >
          {({
            imageList,
            onImageUpload,
            onImageRemove,
            onImageUpdate,
            dragProps,
          }) => (
            // write your building UI
            <Stack className="upload__image-wrapper">
              {imageList.length === 0 ? (
                <Stack
                  pos="relative"
                  bg="blue.0"
                  onClick={onImageUpload}
                  {...dragProps}
                  styles={{
                    root: {
                      width: "120px",
                      height: "120px",
                      borderRadius: "100px",
                      border: "0",
                    },
                  }}
                  justify="center"
                  align="center"
                >
                  <Stack justify="center" align="center">
                    <LoadingOverlay visible={uploaderLoading} />
                    <IconUser
                      size={85}
                      stroke={1}
                      color="var(--mantine-color-blue-5)"
                    />
                    <ActionIcon
                      variant="filled"
                      radius="xl"
                      size="lg"
                      color="gray.5"
                      pos="absolute"
                      bottom="8px"
                      left="-8px"
                      styles={{
                        root: {
                          border: "4px solid white",
                        },
                      }}
                    >
                      <IconPencil size={20} />
                    </ActionIcon>
                  </Stack>
                </Stack>
              ) : (
                imageList.map((image, index) => (
                  <>
                    <Stack
                      pos="relative"
                      bg="blue.0"
                      onClick={() => onImageUpdate(index)}
                      {...dragProps}
                      styles={{
                        root: {
                          width: "120px",
                          height: "120px",
                          borderRadius: "100px",
                          border: "0",
                        },
                      }}
                      justify="center"
                      align="center"
                    >
                      <Stack justify="center" align="center">
                        <LoadingOverlay visible={uploaderLoading} />
                        <Image
                          src={image["data_url"]}
                          h={120}
                          w={120}
                          radius="100%"
                          fit="cover"
                        />
                        <ActionIcon
                          variant="filled"
                          radius="xl"
                          size="lg"
                          color="gray.5"
                          pos="absolute"
                          bottom="8px"
                          left="-8px"
                          onClick={(e) => {
                            e.stopPropagation();
                            onImageRemove(index);
                          }}
                          styles={{
                            root: {
                              border: "4px solid white",
                            },
                          }}
                        >
                          <IconTrash size={20} />
                        </ActionIcon>
                      </Stack>
                    </Stack>
                  </>
                ))
              )}
            </Stack>
          )}
        </ImageUploading>

        <Grid>
          <Grid.Col span={6}>
            <TextInput
              size="lg"
              withAsterisk
              styles={{
                label: {
                  fontSize: "1rem",
                },
              }}
              placeholder=""
              label="نام"
              {...form.getInputProps("FirstName")}
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <TextInput
              withAsterisk
              size="lg"
              styles={{
                label: {
                  fontSize: "1rem",
                },
              }}
              placeholder=""
              label="نام خانوادگی"
              {...form.getInputProps("LastName")}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <TextInput
              size="lg"
              styles={{
                label: {
                  fontSize: "1rem",
                },
              }}
              placeholder=""
              label="عنوان شغلی"
              description="به عنوان مثال : ناخن کار ، براشینگ و ..."
              {...form.getInputProps("JobTitle")}
            />
          </Grid.Col>
        </Grid>
        <InputBase
          size="lg"
          type="tel"
          dir="ltr"
          label="شماره تلفن"
          readOnly={readonly}
          styles={{
            input: {
              textAlign: "left",
              paddingLeft: "16px",
            },
            label: {
              fontSize: "1rem",
            },
          }}
          value={form.values.MobileNumber || ""}
          maxLength={11}
          withAsterisk
          onChange={(e) => {
            form.setValues({
              MobileNumber: PersianUtil.currentNumberToEnglish(e.target.value),
            });
          }}
          error={form.errors.MobileNumber}
        />

        <Grid>
          <Grid.Col span={12}>
            <Select
              size="lg"
              placeholder=""
              required
              styles={{
                label: {
                  fontSize: "1rem",
                },
              }}
              label="جنسیت"
              {...form.getInputProps("genderForm")}
              data={[
                {
                  value: Gender.Value1.toString(),
                  label: "خانم",
                },
                {
                  value: Gender.Value0.toString(),
                  label: "مرد",
                },
              ]}
            />
          </Grid.Col>

          <BirthDateSelector
            day={form.values.day ?? undefined}
            month={form.values.month ?? undefined}
            year={form.values.year ?? undefined}
            onDayChange={(value) =>
              form.setValues({ day: (value ?? "") as string })
            }
            onMonthChange={(value) =>
              form.setValues({ month: (value ?? "") as string })
            }
            onYearChange={(value) =>
              form.setValues({ year: (value ?? "") as string })
            }
            dayError={
              typeof form.errors.day === "string" ? form.errors.day : undefined
            }
            monthError={
              typeof form.errors.month === "string"
                ? form.errors.month
                : undefined
            }
            yearError={
              typeof form.errors.year === "string"
                ? form.errors.year
                : undefined
            }
            size="lg"
          />
          <Grid.Col span={12}>
            {form.errors["all-or-none-date"] && (
              <Text>تاریخ تولد را وارد نمایید</Text>
            )}
          </Grid.Col>

          <Grid.Col span={12}>
            <Title order={5}> رنگ تقویم </Title>

            <ColorPicker
              format="hex"
              withPicker={false}
              {...form.getInputProps("CalendarColor")}
              fullWidth
              styles={{
                swatches: {
                  gap: "5px",
                },
                swatch: {
                  "--cs-radius": "100px",
                  flex: "none",
                  padding: "20px",
                  width: "20px",
                  height: "20px",
                },
              }}
              swatches={[
                ...DEFAULT_THEME.colors.red.slice(3, 4),
                ...DEFAULT_THEME.colors.green.slice(3, 4),
                ...DEFAULT_THEME.colors.blue.slice(3, 4),
                ...DEFAULT_THEME.colors.yellow.slice(3, 4),
                ...DEFAULT_THEME.colors.gray.slice(3, 4),
                ...DEFAULT_THEME.colors.lime.slice(3, 4),
                ...DEFAULT_THEME.colors.pink.slice(3, 4),
                ...DEFAULT_THEME.colors.violet.slice(3, 4),
                ...DEFAULT_THEME.colors.orange.slice(3, 4),
                ...DEFAULT_THEME.colors.cyan.slice(3, 4),
                ...DEFAULT_THEME.colors.indigo.slice(3, 4),
              ]}
            />
          </Grid.Col>
        </Grid>
      </Stack>
    </Stack>
  );
}

export default TeamMemberProfileInformation;
